import differenceInMinutes from 'date-fns/difference_in_minutes';
import _ from 'lodash';
import { ICancellationPolicy } from '../schemas/cancellationPolicy';
import { IReservation } from '../schemas/reservation';
import { IService } from '../schemas/service';
import { getCurrentPeriod } from './service';

/**
 * Find the appropriate cancellation policy from a reservation for a given `atDate`
 * @param reservation the reservation to use
 * @param service the service in picture
 * @param atDate the date for which we want to find the cancellation policy
 */
export function getCancellationPolicy(
  reservation: IReservation,
  service: IService,
  atDate: Date = new Date(),
): ICancellationPolicy | undefined {
  const { serviceOption } = reservation;
  if (serviceOption) {
    if (!serviceOption.cancellable) return undefined;
    if (!serviceOption.cancellationPolicies || _.isEmpty(serviceOption.cancellationPolicies)) {
      return undefined;
    }
    return getClosestPolicy(serviceOption.cancellationPolicies, reservation.startDate, atDate);
  }

  const period = reservation.period || getCurrentPeriod(service, atDate);
  if (period) {
    if (!period.cancellable) return undefined;
    if (!period.cancellationPolicies || _.isEmpty(period.cancellationPolicies)) {
      return undefined;
    }
    return getClosestPolicy(period.cancellationPolicies, reservation.startDate, atDate);
  }
  return undefined;
}

/**
 * Finds the closest cancellation policy for `atDate`
 * @param cancellationPolicies possible cancellation policies
 * @param bookingDate the date on which the user is going to check-in/avail the service
 * @param atDate the date for which we have to find the cancellation policy
 */
function getClosestPolicy(
  cancellationPolicies: ICancellationPolicy[],
  bookingDate: Date,
  atDate: Date,
) {
  const diffInMinutes = differenceInMinutes(bookingDate, atDate);
  const possiblePolicies = cancellationPolicies.filter(policy => policy.duration <= diffInMinutes);
  const closestPolicy = _.maxBy(possiblePolicies, policy => policy.duration);
  return closestPolicy;
}

export function calculateCancellationCharge(
  cancellationPolicy: ICancellationPolicy,
  paidServicePrice: number,
) {
  if (cancellationPolicy.refundType === 'fixed') {
    return cancellationPolicy.refundAmount;
  } else if (cancellationPolicy.refundType === 'percent') {
    return _.round((cancellationPolicy.refundAmount * paidServicePrice) / 100, 2);
  }
  return paidServicePrice;
}
